import { useCallback } from 'react'
import type { Dispatch } from 'react'
import type { CustomerOrder } from '../../../customerOrdersApi'
import type { OrderDateFilterOption, OrderPageSizeOption, OrderSortOption, OrderVisualStatus } from '../ordersListShared'
import type { OrdersListFlowAction } from './OrdersListFlow.types'

type UseOrdersListFlowSubmitHandlersInput = {
  dispatch: Dispatch<OrdersListFlowAction>
  filtersOpen: boolean
  onNavigate: (targetPath: string) => void
  toLocalizedPath: (path: string) => string
  buildSupportRequestPath: (order: CustomerOrder) => string
}

type UseOrdersListFlowSubmitHandlersOutput = {
  onSearchChange: (value: string) => void
  onToggleFilters: () => void
  onSortChange: (value: OrderSortOption) => void
  onDateFilterChange: (value: OrderDateFilterOption) => void
  onCustomDateFromChange: (value: string) => void
  onCustomDateToChange: (value: string) => void
  onToggleStatusFilter: (status: OrderVisualStatus, checked: boolean) => void
  onPriceMinChange: (value: string) => void
  onPriceMaxChange: (value: string) => void
  onResetDateFilter: () => void
  onRemoveStatusFilter: (status: OrderVisualStatus) => void
  onResetPriceRange: () => void
  onClearAllFilters: () => void
  onSetPageSize: (value: OrderPageSizeOption) => void
  onSetCurrentPage: (value: number) => void
  onOpenOrderDetail: (orderId: string) => void
  onBuyAgain: (order: CustomerOrder) => void
  onRequestSupport: (order: CustomerOrder) => void
  onOpenCatalog: () => void
}

export const useOrdersListFlowSubmitHandlers = ({
  dispatch,
  filtersOpen,
  onNavigate,
  toLocalizedPath,
  buildSupportRequestPath,
}: UseOrdersListFlowSubmitHandlersInput): UseOrdersListFlowSubmitHandlersOutput => {
  const onSearchChange = useCallback((value: string) => {
    dispatch({ type: 'SET_SEARCH_VALUE', value })
  }, [dispatch])

  const onToggleFilters = useCallback(() => {
    dispatch({ type: 'SET_FILTERS_OPEN', value: !filtersOpen })
  }, [dispatch, filtersOpen])

  const onSortChange = useCallback((value: OrderSortOption) => {
    dispatch({ type: 'SET_SORT_BY', value })
  }, [dispatch])

  const onDateFilterChange = useCallback((value: OrderDateFilterOption) => {
    dispatch({ type: 'SET_DATE_FILTER', value })
  }, [dispatch])

  const onCustomDateFromChange = useCallback((value: string) => {
    dispatch({ type: 'SET_CUSTOM_DATE_FROM', value })
  }, [dispatch])

  const onCustomDateToChange = useCallback((value: string) => {
    dispatch({ type: 'SET_CUSTOM_DATE_TO', value })
  }, [dispatch])

  const onToggleStatusFilter = useCallback((status: OrderVisualStatus, checked: boolean) => {
    dispatch({ type: 'TOGGLE_STATUS_FILTER', status, checked })
  }, [dispatch])

  const onPriceMinChange = useCallback((value: string) => {
    dispatch({ type: 'SET_PRICE_MIN', value })
  }, [dispatch])

  const onPriceMaxChange = useCallback((value: string) => {
    dispatch({ type: 'SET_PRICE_MAX', value })
  }, [dispatch])

  const onClearAllFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_FILTERS' })
  }, [dispatch])

  const onResetDateFilter = useCallback(() => {
    dispatch({ type: 'RESET_DATE_FILTER' })
  }, [dispatch])

  const onRemoveStatusFilter = useCallback((status: OrderVisualStatus) => {
    dispatch({ type: 'TOGGLE_STATUS_FILTER', status, checked: false })
  }, [dispatch])

  const onResetPriceRange = useCallback(() => {
    dispatch({ type: 'RESET_PRICE_RANGE' })
  }, [dispatch])

  const onSetPageSize = useCallback((value: OrderPageSizeOption) => {
    dispatch({ type: 'SET_PAGE_SIZE', value })
  }, [dispatch])

  const onSetCurrentPage = useCallback((value: number) => {
    dispatch({ type: 'SET_CURRENT_PAGE', value })
  }, [dispatch])

  const onOpenOrderDetail = useCallback((orderId: string) => {
    onNavigate(toLocalizedPath(`/dashboard/orders/${encodeURIComponent(orderId)}`))
  }, [onNavigate, toLocalizedPath])

  const onBuyAgain = useCallback((order: CustomerOrder) => {
    const firstOrderProduct = order.items.find((item) => item.productSlug.trim().length > 0)
    const rebuyPath = firstOrderProduct
      ? toLocalizedPath(`/${encodeURIComponent(firstOrderProduct.productSlug)}`)
      : toLocalizedPath('/')
    onNavigate(rebuyPath)
  }, [onNavigate, toLocalizedPath])

  const onRequestSupport = useCallback((order: CustomerOrder) => {
    onNavigate(buildSupportRequestPath(order))
  }, [buildSupportRequestPath, onNavigate])

  const onOpenCatalog = useCallback(() => {
    onNavigate(toLocalizedPath('/'))
  }, [onNavigate, toLocalizedPath])

  return {
    onSearchChange,
    onToggleFilters,
    onSortChange,
    onDateFilterChange,
    onCustomDateFromChange,
    onCustomDateToChange,
    onToggleStatusFilter,
    onPriceMinChange,
    onPriceMaxChange,
    onResetDateFilter,
    onRemoveStatusFilter,
    onResetPriceRange,
    onClearAllFilters,
    onSetPageSize,
    onSetCurrentPage,
    onOpenOrderDetail,
    onBuyAgain,
    onRequestSupport,
    onOpenCatalog,
  }
}
